import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader, RevealPasswordModal } from '../components/common';
import { ROUTES } from '../constants';
import { merchantService, ApiError } from '../services';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

// Partita Iva = exactly 11 numeric digits (Italian VAT format).
const PIVA_PATTERN = /^\d{11}$/;
const PIVA_FORMAT_ERROR = 'Partita Iva must be exactly 11 digits.';

const billingPayloadFromMerchant = m => ({
  billingPartitaIva: (m.partitaIva ?? m.billingPartitaIva ?? '').trim(),
  billingLegalCompanyName: (m.billingLegalCompanyName ?? '').trim(),
  billingAddress: (m.billingAddress ?? '').trim(),
  billingTown: (m.billingTown ?? '').trim(),
  billingProvince: (m.billingProvince ?? '').trim(),
  billingZipCode: (m.billingZipCode ?? '').trim(),
  billingSdiCode: (m.billingSdiCode ?? '').trim(),
  billingPecEmail: (m.billingPecEmail ?? '').trim(),
});

const MerchantEditBillingPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const merchantFromState = location.state?.merchant;

  // Header info can render immediately from route state, but form values must
  // come from a fresh fetch so we know the current `revealed` flag.
  const [merchant, setMerchant] = useState(merchantFromState || null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [validateError, setValidateError] = useState(null);
  const [pendingAutoFill, setPendingAutoFill] = useState(null);
  const [validatedMeta, setValidatedMeta] = useState(null);
  const [stripeSync, setStripeSync] = useState(null);

  const [fieldErrors, setFieldErrors] = useState({});
  const initialFetchDoneRef = useRef(false);
  // RevealPasswordModal calls onSuccess THEN onClose on a successful verify;
  // this ref tells onClose to skip the bounce-back redirect in that case.
  const justVerifiedRef = useRef(false);

  const validate = useCallback(currentForm => {
    if (!currentForm) return {};
    const requiredFields = [
      'billingPartitaIva',
      'billingLegalCompanyName',
      'billingAddress',
      'billingTown',
      'billingProvince',
      'billingZipCode',
      'billingSdiCode',
      'billingPecEmail',
    ];

    const nextErrors = {};
    for (const key of requiredFields) {
      const value = String(currentForm[key] ?? '').trim();
      if (!value) nextErrors[key] = 'This field is required.';
    }
    // Format check overrides the generic "required" error for PIVA.
    const piva = String(currentForm.billingPartitaIva ?? '').trim();
    if (piva && !PIVA_PATTERN.test(piva)) {
      nextErrors.billingPartitaIva = PIVA_FORMAT_ERROR;
    }
    return nextErrors;
  }, []);

  const loadMerchant = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await merchantService.getFormattedMerchantDetails(id);
      if (response?.data) {
        const data = response.data;
        setMerchant(data);
        const isRevealed = data.revealed === true;
        setRevealed(isRevealed);
        if (isRevealed) {
          setForm(billingPayloadFromMerchant(data));
        } else {
          // Don't pre-fill with masked values; wait for reveal then refetch.
          setForm(billingPayloadFromMerchant({}));
          setRevealModalOpen(true);
        }
      }
    } catch (err) {
      console.error('Failed to load merchant:', err);
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to load merchant. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (initialFetchDoneRef.current) return;
    initialFetchDoneRef.current = true;
    loadMerchant();
  }, [loadMerchant]);

  useEffect(() => {
    if (!loading && !merchant && error) {
      navigate(ROUTES.MERCHANTS, { replace: true });
    }
  }, [loading, merchant, error, navigate]);

  const handleChange = field => e => {
    const nextValue = e.target.value;
    setForm(prev => ({ ...prev, [field]: nextValue }));
    setFieldErrors(prev => {
      if (!prev?.[field]) return prev;
      const trimmed = String(nextValue ?? '').trim();
      if (!trimmed) return prev;
      const { [field]: _ignored, ...rest } = prev;
      return rest;
    });
    if (stripeSync) setStripeSync(null);
  };

  const goBackToDetail = () => {
    if (!id) {
      navigate(ROUTES.MERCHANTS);
      return;
    }
    navigate(ROUTES.generatePath(ROUTES.MERCHANTS_DETAIL, { id }), {
      state: merchant ? { merchant } : undefined,
    });
  };

  const otherBillingFieldsFilled = () => {
    if (!form) return false;
    const others = [
      'billingLegalCompanyName',
      'billingAddress',
      'billingTown',
      'billingProvince',
      'billingZipCode',
      'billingSdiCode',
      'billingPecEmail',
    ];
    return others.some(k => String(form[k] ?? '').trim() !== '');
  };

  const applyAutoFill = data => {
    setForm(prev => ({
      ...prev,
      billingPartitaIva: data.billingPartitaIva ?? prev.billingPartitaIva,
      billingLegalCompanyName: data.billingLegalCompanyName ?? prev.billingLegalCompanyName,
      billingAddress: data.billingAddress ?? prev.billingAddress,
      billingTown: data.billingTown ?? prev.billingTown,
      billingProvince: data.billingProvince ?? prev.billingProvince,
      billingZipCode: data.billingZipCode ?? prev.billingZipCode,
      billingSdiCode: data.billingSdiCode ?? prev.billingSdiCode,
      billingPecEmail: data.billingPecEmail ?? prev.billingPecEmail,
    }));
    setValidatedMeta({
      billingPartitaIvaChecked: data.billingPartitaIvaChecked ?? false,
      billingPartitaIvaRawResponse: data.billingPartitaIvaRawResponse ?? {},
    });
    setFieldErrors({});
  };

  const handleValidate = async () => {
    if (!id || !form) return;
    const partitaIva = form.billingPartitaIva.trim();
    if (!partitaIva) {
      setValidateError('Please enter a Partita IVA before validating.');
      return;
    }
    if (!PIVA_PATTERN.test(partitaIva)) {
      setValidateError(PIVA_FORMAT_ERROR);
      return;
    }
    setValidating(true);
    setValidateError(null);
    setPendingAutoFill(null);
    try {
      const response = await merchantService.validatePartitaIva(id, partitaIva);
      if (response?.data) {
        // Stash checked + rawResponse the moment validate succeeds — these
        // describe the Partita IVA itself, not the address. They must survive
        // even if admin picks Keep Current (which only declines the address
        // autofill). Editing the Partita IVA input clears this stash.
        setValidatedMeta({
          billingPartitaIvaChecked: response.data.billingPartitaIvaChecked ?? true,
          billingPartitaIvaRawResponse:
            response.data.billingPartitaIvaRawResponse ?? {},
        });
        if (otherBillingFieldsFilled()) {
          setPendingAutoFill(response.data);
        } else {
          applyAutoFill(response.data);
        }
      }
    } catch (err) {
      console.error('Failed to validate Partita IVA:', err);
      setValidateError(
        err instanceof ApiError
          ? err.message
          : 'Failed to validate Partita IVA. Please try again.'
      );
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!id || !form) return;
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      setError('All fields are required.');
      return;
    }
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirmSave = async () => {
    if (!id || !form) return;
    setShowConfirm(false);
    setSaving(true);
    setError(null);
    setStripeSync(null);
    try {
      const response = await merchantService.updatePartnerBilling(id, {
        billingPartitaIva: form.billingPartitaIva.trim(),
        billingLegalCompanyName: form.billingLegalCompanyName.trim(),
        billingAddress: form.billingAddress.trim(),
        billingTown: form.billingTown.trim(),
        billingProvince: form.billingProvince.trim(),
        billingZipCode: form.billingZipCode.trim(),
        billingSdiCode: form.billingSdiCode.trim(),
        billingPecEmail: form.billingPecEmail.trim(),
        billingPartitaIvaChecked: validatedMeta?.billingPartitaIvaChecked ?? false,
        billingPartitaIvaRawResponse: validatedMeta?.billingPartitaIvaRawResponse ?? {},
      });
      // Stay on page — show sync-status banner like the other Edit pages.
      // BE's stripeCustomerSyncMessage is friendly enough to render verbatim
      // (covers both "updated" and "skipped" cases).
      const status = response?.data?.stripeCustomerSyncStatus || 'updated';
      const message =
        response?.data?.stripeCustomerSyncMessage ||
        'Billing details updated successfully.';
      setStripeSync({ status, message });
      // Mirror saved values into the merchant cache so the detail page sees
      // the new values when admin clicks Back to Details.
      setMerchant(prev =>
        prev
          ? {
              ...prev,
              partitaIva: form.billingPartitaIva.trim(),
              billingPartitaIva: form.billingPartitaIva.trim(),
              billingLegalCompanyName: form.billingLegalCompanyName.trim(),
              billingAddress: form.billingAddress.trim(),
              billingTown: form.billingTown.trim(),
              billingProvince: form.billingProvince.trim(),
              billingZipCode: form.billingZipCode.trim(),
              billingSdiCode: form.billingSdiCode.trim(),
              billingPecEmail: form.billingPecEmail.trim(),
            }
          : prev
      );
    } catch (err) {
      console.error('Failed to update billing:', err);
      setError(
        err instanceof ApiError
          ? err.message
          : 'Failed to save billing details. Please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form || !merchant) {
    return (
      <div className='flex items-center justify-center min-h-[40vh]'>
        <div className='flex items-center space-x-2'>
          <svg
            className='animate-spin h-8 w-8 text-blue-600'
            fill='none'
            viewBox='0 0 24 24'
          >
            <circle
              className='opacity-25'
              cx='12'
              cy='12'
              r='10'
              stroke='currentColor'
              strokeWidth='4'
            />
            <path
              className='opacity-75'
              fill='currentColor'
              d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
            />
          </svg>
          <span className='text-gray-600'>Loading…</span>
        </div>
      </div>
    );
  }

  const detailHref = ROUTES.generatePath(ROUTES.MERCHANTS_DETAIL, { id });
  const hasFieldError = key => !!fieldErrors?.[key];
  const inputClassFor = key =>
    `${inputClass} ${
      hasFieldError(key)
        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
        : ''
    }`;
  // Form is editable only after reveal succeeded — same pattern as the
  // marketer/shopper Edit Bank / Partita Iva / Codice Fiscale pages.
  const editable = revealed && !saving;

  return (
    <div>
      <PageHeader
        title='Edit Billing Details'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Merchants', href: ROUTES.MERCHANTS },
          { name: merchant.companyName, href: detailHref, state: { merchant } },
          { name: 'Edit Billing Details' },
        ]}
      />

      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
          <p className='text-sm text-red-700'>{error}</p>
        </div>
      )}

      {stripeSync && (
        <div className='bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6 flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-amber-800'>{stripeSync.message}</p>
          <button
            type='button'
            onClick={goBackToDetail}
            className='px-3 py-1.5 border border-amber-300 rounded-md text-xs font-medium text-amber-800 bg-white hover:bg-amber-50 whitespace-nowrap'
          >
            Back to Details
          </button>
        </div>
      )}

      {pendingAutoFill && (
        <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex flex-wrap items-center justify-between gap-3'>
          <p className='text-sm text-blue-800'>
            Validation successful. Replace the current billing data with the suggested values?
          </p>
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={() => setPendingAutoFill(null)}
              className='px-3 py-1.5 border border-blue-300 rounded-md text-xs font-medium text-blue-700 bg-white hover:bg-blue-50'
            >
              Keep Current
            </button>
            <button
              type='button'
              onClick={() => { applyAutoFill(pendingAutoFill); setPendingAutoFill(null); }}
              className='px-3 py-1.5 border border-transparent rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700'
            >
              Replace
            </button>
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className='bg-white shadow-sm border border-gray-200 rounded-lg'
      >
        <div className='px-4 md:px-6 py-5 border-b border-gray-200'>
          <h3 className='text-lg font-semibold text-gray-900'>
            {merchant.companyName || 'Merchant'}
          </h3>
          <p className='text-xs text-gray-500 mt-0.5 break-all'>
            Merchant ID: {merchant.id || '-'}
          </p>
        </div>

        <div className='px-6 py-6 md:py-8'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5'>
          <div>
            <label
              htmlFor='billingPartitaIva'
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              Partita Iva <span className='text-red-600'>*</span>
            </label>
            <div className='flex gap-2'>
              <input
                id='billingPartitaIva'
                type='text'
                value={form.billingPartitaIva}
                onChange={e => {
                  handleChange('billingPartitaIva')(e);
                  setValidateError(null);
                  setPendingAutoFill(null);
                  setValidatedMeta(null);
                }}
                disabled={!editable}
                className={`flex-1 ${inputClassFor('billingPartitaIva')}`}
                autoComplete='off'
                aria-invalid={hasFieldError('billingPartitaIva')}
              />
              <button
                type='button'
                onClick={handleValidate}
                disabled={!editable || validating}
                className='px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'
              >
                {validating ? 'Validating…' : 'Validate'}
              </button>
            </div>
            {hasFieldError('billingPartitaIva') && (
              <p className='mt-1 text-xs text-red-600'>
                {fieldErrors.billingPartitaIva}
              </p>
            )}
            {validateError && (
              <p className='mt-1 text-xs text-red-600'>{validateError}</p>
            )}
          </div>
          <div>
            <label
              htmlFor='billingLegalCompanyName'
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              Legal Company Name <span className='text-red-600'>*</span>
            </label>
            <input
              id='billingLegalCompanyName'
              type='text'
              value={form.billingLegalCompanyName}
              onChange={handleChange('billingLegalCompanyName')}
              disabled={!editable}
              className={inputClassFor('billingLegalCompanyName')}
              autoComplete='organization'
              aria-invalid={hasFieldError('billingLegalCompanyName')}
            />
            {hasFieldError('billingLegalCompanyName') && (
              <p className='mt-1 text-xs text-red-600'>
                {fieldErrors.billingLegalCompanyName}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor='billingAddress'
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              Billing Address <span className='text-red-600'>*</span>
            </label>
            <input
              id='billingAddress'
              type='text'
              value={form.billingAddress}
              onChange={handleChange('billingAddress')}
              disabled={!editable}
              className={inputClassFor('billingAddress')}
              autoComplete='street-address'
              aria-invalid={hasFieldError('billingAddress')}
            />
            {hasFieldError('billingAddress') && (
              <p className='mt-1 text-xs text-red-600'>
                {fieldErrors.billingAddress}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor='billingTown'
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              City <span className='text-red-600'>*</span>
            </label>
            <input
              id='billingTown'
              type='text'
              value={form.billingTown}
              onChange={handleChange('billingTown')}
              disabled={!editable}
              className={inputClassFor('billingTown')}
              autoComplete='address-level2'
              aria-invalid={hasFieldError('billingTown')}
            />
            {hasFieldError('billingTown') && (
              <p className='mt-1 text-xs text-red-600'>
                {fieldErrors.billingTown}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor='billingProvince'
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              Province <span className='text-red-600'>*</span>
            </label>
            <input
              id='billingProvince'
              type='text'
              value={form.billingProvince}
              onChange={handleChange('billingProvince')}
              disabled={!editable}
              className={inputClassFor('billingProvince')}
              autoComplete='address-level1'
              aria-invalid={hasFieldError('billingProvince')}
            />
            {hasFieldError('billingProvince') && (
              <p className='mt-1 text-xs text-red-600'>
                {fieldErrors.billingProvince}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor='billingZipCode'
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              ZIP Code <span className='text-red-600'>*</span>
            </label>
            <input
              id='billingZipCode'
              type='text'
              value={form.billingZipCode}
              onChange={handleChange('billingZipCode')}
              disabled={!editable}
              className={inputClassFor('billingZipCode')}
              autoComplete='postal-code'
              aria-invalid={hasFieldError('billingZipCode')}
            />
            {hasFieldError('billingZipCode') && (
              <p className='mt-1 text-xs text-red-600'>
                {fieldErrors.billingZipCode}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor='billingSdiCode'
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              SDI Code <span className='text-red-600'>*</span>
            </label>
            <input
              id='billingSdiCode'
              type='text'
              value={form.billingSdiCode}
              onChange={handleChange('billingSdiCode')}
              disabled={!editable}
              className={inputClassFor('billingSdiCode')}
              autoComplete='off'
              aria-invalid={hasFieldError('billingSdiCode')}
            />
            {hasFieldError('billingSdiCode') && (
              <p className='mt-1 text-xs text-red-600'>
                {fieldErrors.billingSdiCode}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor='billingPecEmail'
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              PEC <span className='text-red-600'>*</span>
            </label>
            <input
              id='billingPecEmail'
              type='email'
              value={form.billingPecEmail}
              onChange={handleChange('billingPecEmail')}
              disabled={!editable}
              className={inputClassFor('billingPecEmail')}
              autoComplete='email'
              aria-invalid={hasFieldError('billingPecEmail')}
            />
            {hasFieldError('billingPecEmail') && (
              <p className='mt-1 text-xs text-red-600'>
                {fieldErrors.billingPecEmail}
              </p>
            )}
          </div>
        </div>

        <div className='mt-10 flex flex-wrap items-center justify-center gap-3'>
          <button
            type='button'
            onClick={goBackToDetail}
            disabled={saving}
            className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50'
          >
            Cancel
          </button>
          <button
            type='submit'
            disabled={!editable}
            className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
        </div>
      </form>

      {showConfirm && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
          <div className='bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6'>
            <h2 className='text-base font-semibold text-gray-900 mb-3'>
              Confirm Partita Iva Update
            </h2>
            <p className='text-sm text-gray-600 mb-6'>
              Please confirm the update to the merchant's billing details.
              Under GDPR, you are responsible for ensuring this data is
              accurate and up to date. This change will be logged with your
              user ID and a timestamp.
            </p>
            <div className='flex justify-end gap-3'>
              <button
                type='button'
                onClick={() => setShowConfirm(false)}
                className='px-5 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleConfirmSave}
                className='px-5 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <RevealPasswordModal
        isOpen={revealModalOpen}
        onClose={() => {
          setRevealModalOpen(false);
          if (justVerifiedRef.current) {
            justVerifiedRef.current = false;
            return;
          }
          // If still not revealed (user cancelled), reveal is required to
          // edit billing — bounce back to the merchant detail page.
          if (!revealed) goBackToDetail();
        }}
        onSuccess={async () => {
          justVerifiedRef.current = true;
          // Refetch with reveal active so the form pre-fills with raw values.
          await loadMerchant();
        }}
      />
    </div>
  );
};

export default MerchantEditBillingPage;
